import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  Wallet,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Search,
  Eye,
  Plus,
  Minus,
  Download,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const AdminWalletManagement = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showAdjustment, setShowAdjustment] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/wallets`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setWallets(data.wallets || []);
      } else {
        setError('Failed to load wallet data');
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet information');
    } finally {
      setLoading(false);
    }
  };

  const handleWalletAdjustment = async () => {
    if (!selectedWallet || !adjustmentReason.trim()) {
      setError('Please provide a reason for the adjustment');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/wallets/${selectedWallet.id}/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          amount_cents: Math.round(adjustmentAmount * 100),
          reason: adjustmentReason
        })
      });

      const data = await response.json();

      if (response.ok) {
        await fetchWalletData(); // Refresh data
        setShowAdjustment(false);
        setSelectedWallet(null);
        setAdjustmentAmount(0);
        setAdjustmentReason('');
      } else {
        setError(data.detail || 'Failed to adjust wallet');
      }
    } catch (err) {
      console.error('Adjustment error:', err);
      setError('Failed to adjust wallet balance');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
      case 'low_balance': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
      case 'blocked': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'low_balance': return <AlertCircle className="w-4 h-4" />;
      case 'blocked': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const filteredWallets = wallets.filter(wallet => 
    wallet.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wallet.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading wallet management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Wallet Management</h2>
          <p className="text-gray-600 dark:text-gray-300">Manage user wallets and financial operations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchWalletData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by user name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          </div>
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
        </div>
      </div>

      {/* Wallets Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center">
            <Wallet className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Total Wallets</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{wallets.length}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Total Balance</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                ${wallets.reduce((sum, w) => sum + (w.balance_cents || 0), 0) / 100}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Active Wallets</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {wallets.filter(w => w.status === 'active').length}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Low Balance</div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {wallets.filter(w => w.status === 'low_balance').length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wallets Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User Wallets</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
              {filteredWallets.map((wallet) => (
                <tr key={wallet.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {wallet.user_name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {wallet.user_email || 'No email'}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      ${((wallet.balance_cents || 0) / 100).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      USD
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(wallet.status)}`}>
                      {getStatusIcon(wallet.status)}
                      {wallet.status?.charAt(0).toUpperCase() + wallet.status?.slice(1).replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {wallet.last_transaction_at ? new Date(wallet.last_transaction_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setSelectedWallet(wallet)}
                        variant="outline"
                        size="sm"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedWallet(wallet);
                          setShowAdjustment(true);
                        }}
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredWallets.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">No wallets found</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? 'Try adjusting your search criteria' : 'No user wallets available'}
            </p>
          </div>
        )}
      </div>

      {/* Wallet Adjustment Modal */}
      {showAdjustment && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md dark:bg-gray-900">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">
              Adjust Wallet Balance
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 dark:bg-gray-800">
                <div className="text-sm text-gray-600 dark:text-gray-300">User:</div>
                <div className="font-medium text-gray-900 dark:text-gray-100">{selectedWallet.user_name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{selectedWallet.user_email}</div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 dark:bg-gray-800">
                <div className="text-sm text-gray-600 dark:text-gray-300">Current Balance:</div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  ${((selectedWallet.balance_cents || 0) / 100).toFixed(2)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Adjustment Amount (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={adjustmentAmount}
                  onChange={(e) => setAdjustmentAmount(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  placeholder="Enter positive or negative amount"
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Positive amounts add funds, negative amounts deduct funds
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Adjustment
                </label>
                <textarea
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  rows={3}
                  placeholder="Explain the reason for this adjustment..."
                  required
                />
              </div>

              {adjustmentAmount !== 0 && (
                <div className="bg-blue-50 rounded-lg p-3 dark:bg-blue-900/20">
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    New Balance: ${(((selectedWallet.balance_cents || 0) + (adjustmentAmount * 100)) / 100).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleWalletAdjustment}
                disabled={actionLoading || !adjustmentReason.trim() || adjustmentAmount === 0}
                className="flex-1"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : adjustmentAmount > 0 ? (
                  <Plus className="w-4 h-4 mr-2" />
                ) : (
                  <Minus className="w-4 h-4 mr-2" />
                )}
                {adjustmentAmount > 0 ? 'Add Funds' : adjustmentAmount < 0 ? 'Deduct Funds' : 'Adjust'}
              </Button>
              <Button
                onClick={() => {
                  setShowAdjustment(false);
                  setSelectedWallet(null);
                  setAdjustmentAmount(0);
                  setAdjustmentReason('');
                }}
                variant="outline"
                disabled={actionLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWalletManagement;